import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import axios from 'axios';
import { getAuthToken } from "../../../utils/getAuthToken";

// API base URL with fallback
const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

// Async thunk for fetching catalogs
// platform: 'whatsapp' | 'instagram' | 'facebook' — for inbox/workflows; omit or 'whatsapp' keeps existing behavior
export const fetchCatalogs = createAsyncThunk(
  'catalog/fetchCatalogs',
  async (platform, { rejectWithValue }) => {
    try {
      const platformParam = platform && ['instagram', 'facebook', 'whatsapp'].includes(String(platform).toLowerCase())
        ? String(platform).toLowerCase()
        : undefined;
      console.log('🔄 fetchCatalogs: Starting API call to /api/catalogs/get', platformParam ? { platform: platformParam } : '');
      // Get token from memory (not localStorage)
      const token = await getAuthToken();
      console.log('🔑 Token found:', !!token);
      
      if (!token) {
        return rejectWithValue('No token found');
      }
      
      const params = platformParam ? { platform: platformParam } : {};
      const response = await axios.get(`${SERVER_DOMAIN}/api/catalogs/get`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
      });
      
      console.log('✅ fetchCatalogs: API response received:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ fetchCatalogs: API call failed:', error);
      
      // Extract detailed error information from the response
      const errorData = error.response?.data;
      if (errorData) {
        // Return the full error object with details and help information
        return rejectWithValue({
          message: errorData.message || 'Failed to fetch catalogs',
          error: errorData.error || errorData.message,
          details: errorData.details,
          help: errorData.details?.help
        });
      }
      
      return rejectWithValue({
        message: error.message || 'Failed to fetch catalogs',
        error: error.message
      });
    }
  }
);

// Loads catalogs from the DB cache instead of live Meta API -- the fast path for opening the
// Send Catalog modal (inbox) / workflow catalog actions, so that no longer hits Meta on every
// open. Same response shape as fetchCatalogs, so they share the extraReducers cases below;
// server falls back to a live fetch itself the first time a workspace has nothing cached yet.
// Use fetchCatalogs (above) for the explicit "Refresh" action.
export const fetchCatalogsFromDb = createAsyncThunk(
  'catalog/fetchCatalogsFromDb',
  async (platform, { rejectWithValue }) => {
    try {
      const platformParam = platform && ['instagram', 'facebook', 'whatsapp'].includes(String(platform).toLowerCase())
        ? String(platform).toLowerCase()
        : undefined;
      const token = await getAuthToken();
      if (!token) {
        return rejectWithValue('No token found');
      }

      const params = platformParam ? { platform: platformParam } : {};
      const response = await axios.get(`${SERVER_DOMAIN}/api/catalogs/get-cached`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
      });

      return response.data;
    } catch (error) {
      const errorData = error.response?.data;
      if (errorData) {
        return rejectWithValue({
          message: errorData.message || 'Failed to load catalogs',
          error: errorData.error || errorData.message,
          details: errorData.details,
          help: errorData.details?.help
        });
      }

      return rejectWithValue({
        message: error.message || 'Failed to load catalogs',
        error: error.message
      });
    }
  }
);

// Catalog Sets — saved, reusable groups of products from a catalog, sent
// as-is from the inbox composer (see InboxCatalogSend.jsx).
export const fetchCatalogSets = createAsyncThunk(
  'catalog/fetchCatalogSets',
  async (catalogId, { rejectWithValue }) => {
    try {
      const token = await getAuthToken();
      if (!token) return rejectWithValue('No token found');
      const response = await axios.get(`${SERVER_DOMAIN}/api/catalogs/sets`, {
        headers: { Authorization: `Bearer ${token}` },
        params: catalogId ? { catalogId } : {},
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch catalog sets');
    }
  }
);

export const createCatalogSet = createAsyncThunk(
  'catalog/createCatalogSet',
  async ({ name, catalogId, catalogName, products }, { rejectWithValue }) => {
    try {
      const token = await getAuthToken();
      if (!token) return rejectWithValue('No token found');
      const response = await axios.post(
        `${SERVER_DOMAIN}/api/catalogs/sets`,
        { name, catalogId, catalogName, products },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to save catalog set');
    }
  }
);

export const updateCatalogSet = createAsyncThunk(
  'catalog/updateCatalogSet',
  async ({ id, name, catalogId, catalogName, products }, { rejectWithValue }) => {
    try {
      const token = await getAuthToken();
      if (!token) return rejectWithValue('No token found');
      const response = await axios.put(
        `${SERVER_DOMAIN}/api/catalogs/sets/${id}`,
        { name, catalogId, catalogName, products },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update catalog set');
    }
  }
);

export const deleteCatalogSet = createAsyncThunk(
  'catalog/deleteCatalogSet',
  async (setId, { rejectWithValue }) => {
    try {
      const token = await getAuthToken();
      if (!token) return rejectWithValue('No token found');
      await axios.delete(`${SERVER_DOMAIN}/api/catalogs/sets/${setId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return setId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to delete catalog set');
    }
  }
);

// Async thunk for fetching a specific catalog by ID
export const fetchCatalogById = createAsyncThunk(
  'catalog/fetchCatalogById',
  async (catalogId, { rejectWithValue }) => {
    try {
      // Get token from memory (not localStorage)
      const token = await getAuthToken();
      if (!token) {
        return rejectWithValue('No token found');
      }
      
      const response = await axios.get(`${SERVER_DOMAIN}/api/catalogs/get/${catalogId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch catalog');
    }
  }
);

const initialState = {
  catalogs: [],
  selectedCatalog: null,
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  totalCatalogs: 0,
  totalProducts: 0,
  lastFetched: null,
  sets: [],
  setsStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  setsError: null,
};

const catalogSlice = createSlice({
  name: 'catalog',
  initialState,
  reducers: {
    // Clear catalogs
    clearCatalogs: (state) => {
      state.catalogs = [];
      state.selectedCatalog = null;
      state.status = 'idle';
      state.error = null;
      state.totalCatalogs = 0;
      state.totalProducts = 0;
      state.lastFetched = null;
    },
    
    // Set selected catalog
    setSelectedCatalog: (state, action) => {
      state.selectedCatalog = action.payload;
    },
    
    // Clear selected catalog
    clearSelectedCatalog: (state) => {
      state.selectedCatalog = null;
    },
    
    // Update catalog search/filter
    setCatalogSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    
    // Reset catalog state
    resetCatalogState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch catalog by ID
      .addCase(fetchCatalogById.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchCatalogById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.selectedCatalog = action.payload.data;
        state.error = null;
      })
      .addCase(fetchCatalogById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch catalog';
      })

      // Catalog Sets
      .addCase(fetchCatalogSets.pending, (state) => {
        state.setsStatus = 'loading';
        state.setsError = null;
      })
      .addCase(fetchCatalogSets.fulfilled, (state, action) => {
        state.setsStatus = 'succeeded';
        state.sets = action.payload.data?.sets || [];
      })
      .addCase(fetchCatalogSets.rejected, (state, action) => {
        state.setsStatus = 'failed';
        state.setsError = action.payload || 'Failed to fetch catalog sets';
      })
      .addCase(createCatalogSet.fulfilled, (state, action) => {
        const set = action.payload.data?.set;
        if (set) state.sets.unshift(set);
      })
      .addCase(updateCatalogSet.fulfilled, (state, action) => {
        const updated = action.payload.data?.set;
        if (!updated) return;
        const idx = state.sets.findIndex((s) => (s._id || s.id) === (updated._id || updated.id));
        if (idx !== -1) state.sets[idx] = updated;
      })
      .addCase(deleteCatalogSet.fulfilled, (state, action) => {
        state.sets = state.sets.filter((s) => (s.id || s._id) !== action.payload);
      })

      // fetchCatalogs (live Meta + DB sync, used for the explicit "Refresh") and
      // fetchCatalogsFromDb (DB cache, used to populate on open) return the same shape, so they
      // share this handling.
      .addMatcher(isAnyOf(fetchCatalogs.pending, fetchCatalogsFromDb.pending), (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addMatcher(isAnyOf(fetchCatalogs.fulfilled, fetchCatalogsFromDb.fulfilled), (state, action) => {
        state.status = 'succeeded';
        state.catalogs = action.payload.data?.catalogs || [];
        state.totalCatalogs = action.payload.data?.totalCatalogs || 0;
        state.totalProducts = action.payload.data?.totalProducts || 0;
        state.lastFetched = action.payload.data?.fetchedAt || new Date().toISOString();
        state.error = null;
      })
      .addMatcher(isAnyOf(fetchCatalogs.rejected, fetchCatalogsFromDb.rejected), (state, action) => {
        state.status = 'failed';

        // Handle specific error types with detailed information
        const errorPayload = action.payload;
        if (typeof errorPayload === 'string') {
          state.error = errorPayload;
        } else if (errorPayload?.message) {
          // Store the full error object to preserve details and help information
          state.error = errorPayload;
        } else if (errorPayload?.error) {
          state.error = errorPayload.error;
        } else {
          state.error = 'Failed to fetch catalogs';
        }

        state.catalogs = [];
        state.totalCatalogs = 0;
        state.totalProducts = 0;
      });
  },
});

export const { 
  clearCatalogs, 
  setSelectedCatalog, 
  clearSelectedCatalog, 
  setCatalogSearchTerm, 
  resetCatalogState 
} = catalogSlice.actions;

export default catalogSlice.reducer;


